import { projectStatusColors } from '@/utils'
import type { Project, ProjectUsageMetrics } from '../types'

export function calculateProjectUsageMetrics(
  projects: Project[],
): ProjectUsageMetrics {
  if (!projects.length) {
    return {
      totalProjects: 0,
      totalPages: 0,
      activeProjects: 0,
      completedProjects: 0,
      statusBreakdown: [],
      projectsByPages: [],
      avgPagesPerProject: 0,
      projectsOverTime: [],
    }
  }

  const totalProjects = projects.length
  const totalPages = projects.reduce((sum, p) => sum + p.numberOfPages, 0)
  const activeProjects = projects.filter(
    (p) => p.status === 'PROCESSING',
  ).length
  const completedProjects = projects.filter(
    (p) => p.status === 'SUCCEEDED',
  ).length
  const avgPagesPerProject = totalPages / totalProjects

  // Status breakdown
  const statusMap = new Map<string, number>()
  projects.forEach((p) => {
    statusMap.set(p.status, (statusMap.get(p.status) || 0) + 1)
  })

  const statusBreakdown = Array.from(statusMap.entries())
    .map(([status, count]) => ({
      status: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
      count,
      percentage: (count / totalProjects) * 100,
      color:
        projectStatusColors[status as keyof typeof projectStatusColors] ||
        projectStatusColors.NOT_STARTED,
    }))
    .sort((a, b) => b.count - a.count)

  // Projects by pages (top 10)
  const projectsByPages = [...projects]
    .sort((a, b) => b.numberOfPages - a.numberOfPages)
    .slice(0, 10)
    .map((p) => ({
      name: p.name,
      pageCount: p.numberOfPages,
    }))

  // Projects over time (by creation month)
  const monthMap = new Map<string, number>()
  projects.forEach((p) => {
    if (!p.createdAt) return
    const date = new Date(p.createdAt)
    if (Number.isNaN(date.getTime())) return
    const monthLabel = date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    })
    monthMap.set(monthLabel, (monthMap.get(monthLabel) || 0) + 1)
  })

  const projectsOverTime = Array.from(monthMap.entries())
    .map(([month, count]) => ({ month, count }))
    .sort((a, b) => {
      const dateA = new Date(a.month)
      const dateB = new Date(b.month)
      return dateA.getTime() - dateB.getTime()
    })

  return {
    totalProjects,
    totalPages,
    activeProjects,
    completedProjects,
    statusBreakdown,
    projectsByPages,
    avgPagesPerProject,
    projectsOverTime,
  }
}
